import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import {
  getMiPerfil,
  type EmpresaDelPerfil,
  type PlanDelPerfil,
} from '@lib/services/perfil.service';
import { useAuthStore } from '@lib/store/auth.store';

export interface SeleccionCarga {
  empresaId: string;
  planCodigo: string;
  /**
   * Sucursal del plan elegido. El mismo plan puede estar en venta en varias
   * sucursales del empleador: cada una es una opción y el server la exige
   * cuando hay más de una. Vacía si el plan no tiene sucursal configurada.
   */
  sucursalId?: string;
}

/**
 * Con qué organización y plan se va a cargar el padrón, y con qué promotor
 * quedan las altas.
 *
 * Sale todo de `GET /perfil`, que además dice de qué organizaciones esta persona
 * puede cargar (`puedeCargar`: la misma decisión que toma el server al subir —
 * por rol, o porque Binary lo tiene como promotor). Con una sola organización
 * se autocompleta y no se muestra el selector: no hay nada que elegir.
 *
 * Una sola fuente de verdad con el selector global de organización: se
 * arranca en la organización activa (si esta persona puede cargar ahí) y al
 * cambiarla acá se cambia también el selector global. Sin esto las dos
 * selecciones podían diferir sin aviso y el padrón entraba en la organización
 * del selector mientras la pantalla mostraba otra.
 *
 * Es una AYUDA de la pantalla: el server vuelve a resolver empresa, plan y
 * promotor en cada subida, así que lo que se elija acá es un pedido, no una
 * decisión final.
 */
export function ContextoCargaPadron({
  seleccion,
  onChange,
}: {
  seleccion: SeleccionCarga | null;
  onChange: (s: SeleccionCarga | null) => void;
}) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['mi-perfil'],
    queryFn: getMiPerfil,
  });
  const { empresaActivaId, setEmpresaActiva } = useAuthStore();

  // `puedeCargar` ya contempla Oracle caído (el server decide igual que al
  // subir), así que acá no hay nada que adivinar.
  const elegibles = (data?.empresas ?? []).filter((e) => e.puedeCargar);
  const empresa = elegibles.find((e) => e.empresaId === seleccion?.empresaId) ?? null;

  // Autocompletar / corregir la selección cuando cambian los datos o el
  // selector global. La decisión es pura (`decidirSeleccion`); acá solo se aplica.
  useEffect(() => {
    const nueva = decidirSeleccion(elegibles, seleccion, empresaActivaId);
    if (nueva !== undefined) onChange(nueva);
    // `onChange` viene del padre y cambia en cada render: incluirlo dispararía
    // el efecto en loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, seleccion?.empresaId, seleccion?.planCodigo, seleccion?.sucursalId, empresaActivaId]);

  if (isLoading) return <p className="text-element-mid">Cargando organizaciones…</p>;
  if (isError) return <ErrorPerfil error={error} refetch={refetch} isFetching={isFetching} />;
  if (elegibles.length === 0) return <SinOrganizaciones empresas={data?.empresas ?? []} />;

  const planElegido = empresa ? planElegidoDe(empresa, seleccion) : null;

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '4px 0 14px' }}>
      <Campo etiqueta="Organización">
        <SelectorOrganizacion
          elegibles={elegibles}
          empresaId={seleccion?.empresaId ?? ''}
          onElegir={(el) => {
            onChange(sugerida(el));
            // Una sola fuente de verdad: el selector global sigue a esta elección.
            setEmpresaActiva(el.empresaId);
          }}
        />
      </Campo>

      <Campo etiqueta="Plan">
        <SelectorPlan
          empresa={empresa}
          planElegido={planElegido}
          onElegir={(pl) =>
            onChange({
              empresaId: empresa?.empresaId ?? '',
              planCodigo: pl.codigo,
              sucursalId: pl.sucursalId || undefined,
            })
          }
        />
      </Campo>

      <Campo etiqueta="Promotor">
        <Fijo texto={empresa?.promotorUsername || '—'} />
        {empresa && !data?.binaryUsername && empresa.promotorUsername && (
          <div style={{ fontSize: 11.5, color: 'var(--color-element-mid)', marginTop: 2 }}>
            Es el de la organización: tu perfil no tiene promotor propio.
          </div>
        )}
      </Campo>
    </div>
  );
}

/**
 * Qué selección corresponde dado lo que hay: `undefined` = dejar la actual.
 *  - sin organizaciones cargables → `null` (si había algo elegido);
 *  - si el selector global apunta a otra organización cargable → se la sigue;
 *  - si la elegida ya no está entre las cargables → la activa o la primera;
 *  - si el plan elegido (código + sucursal) ya no está en venta → el sugerido.
 */
function decidirSeleccion(
  elegibles: EmpresaDelPerfil[],
  seleccion: SeleccionCarga | null,
  empresaActivaId: string | null,
): SeleccionCarga | null | undefined {
  if (elegibles.length === 0) return seleccion ? null : undefined;
  const activa = elegibles.find((e) => e.empresaId === empresaActivaId);
  const actual = elegibles.find((e) => e.empresaId === seleccion?.empresaId);
  if (activa && actual && activa.empresaId !== actual.empresaId) return sugerida(activa);
  if (!actual) return sugerida(activa ?? elegibles[0]);
  if (!actual.planes.some((pl) => esElElegido(pl, seleccion))) return sugerida(actual);
  return undefined;
}

/** El plan sugerido de una organización, como selección (código + sucursal). */
function sugerida(e: EmpresaDelPerfil): SeleccionCarga {
  return {
    empresaId: e.empresaId,
    planCodigo: e.planCodigoSugerido,
    sucursalId: e.sucursalIdSugerida || undefined,
  };
}

/** La identidad de una opción de plan: código + sucursal. */
function clavePlan(p: Pick<PlanDelPerfil, 'codigo' | 'sucursalId'>): string {
  return `${p.codigo}|${p.sucursalId ?? ''}`;
}

function esElElegido(pl: PlanDelPerfil, sel: SeleccionCarga | null | undefined): boolean {
  return (
    !!sel && pl.codigo === sel.planCodigo && (pl.sucursalId ?? '') === (sel.sucursalId ?? '')
  );
}

/** La fila elegida, o la sugerida mientras el efecto todavía no corrigió la selección. */
function planElegidoDe(
  empresa: EmpresaDelPerfil,
  seleccion: SeleccionCarga | null,
): PlanDelPerfil | null {
  return (
    empresa.planes.find((pl) => esElElegido(pl, seleccion)) ??
    empresa.planes.find((pl) => esElElegido(pl, sugerida(empresa))) ??
    null
  );
}

/**
 * `Nombre (CÓDIGO)`, y la sucursal SOLO cuando el mismo código aparece más de
 * una vez en la lista: ahí es lo único que distingue una opción de otra. Con un
 * código por sucursal (MS) no se agrega ruido.
 */
function etiquetaPlan(p: PlanDelPerfil, todos: PlanDelPerfil[]): string {
  const base = p.nombre ? `${p.nombre} (${p.codigo})` : p.codigo;
  const repetido = todos.filter((x) => x.codigo === p.codigo).length > 1;
  return repetido ? `${base} · suc ${p.sucursalId || '—'}` : base;
}

/**
 * Un error de infraestructura NO es "no tenés organización": se dice qué pasó
 * y se deja reintentar. Antes esto disfrazaba un 5xx de dato faltante y mandaba
 * a reclamar una asignación que ya existía.
 */
function ErrorPerfil({
  error,
  refetch,
  isFetching,
}: {
  error: unknown;
  refetch: () => unknown;
  isFetching: boolean;
}) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return (
    <p
      className="status status-error"
      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
    >
      <AlertTriangle size={16} />
      No se pudo cargar tu perfil{status ? ` (HTTP ${status})` : ''}, así que todavía no se puede
      elegir la organización.
      <button
        type="button"
        className="btn btn-link"
        style={{ padding: 0 }}
        disabled={isFetching}
        onClick={() => {
          refetch();
        }}
      >
        {isFetching ? 'Reintentando…' : 'Reintentar'}
      </button>
    </p>
  );
}

/** Ninguna organización cargable: el motivo lo pone el server (es el mismo que daría el 403 al subir). */
function SinOrganizaciones({ empresas }: { empresas: EmpresaDelPerfil[] }) {
  const detalle = empresas.find((e) => e.detalleCarga)?.detalleCarga;
  const texto =
    empresas.length === 0
      ? 'No tenés ninguna organización asignada, así que no podés subir padrones. Pedile a Medicus que te asigne una.'
      : (detalle ?? 'No estás habilitado para subir padrones de ninguna de tus organizaciones.');
  return (
    <p className="status status-warning" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <AlertTriangle size={16} />
      {texto}
    </p>
  );
}

function SelectorOrganizacion({
  elegibles,
  empresaId,
  onElegir,
}: {
  elegibles: EmpresaDelPerfil[];
  empresaId: string;
  onElegir: (e: EmpresaDelPerfil) => void;
}) {
  if (elegibles.length === 1) return <Fijo texto={elegibles[0].nombre} />;
  return (
    <select
      value={empresaId}
      onChange={(e) => {
        const el = elegibles.find((x) => x.empresaId === e.target.value);
        if (el) onElegir(el);
      }}
      style={selectStyle}
    >
      {elegibles.map((e) => (
        <option key={e.empresaId} value={e.empresaId}>
          {e.nombre}
        </option>
      ))}
    </select>
  );
}

function SelectorPlan({
  empresa,
  planElegido,
  onElegir,
}: {
  empresa: EmpresaDelPerfil | null;
  planElegido: PlanDelPerfil | null;
  onElegir: (p: PlanDelPerfil) => void;
}) {
  if (!empresa) return <Fijo texto="—" />;
  // Un solo plan en venta: no hay nada que elegir.
  if (empresa.planes.length <= 1) {
    return <Fijo texto={planElegido ? etiquetaPlan(planElegido, empresa.planes) : '—'} />;
  }
  return (
    <select
      value={planElegido ? clavePlan(planElegido) : ''}
      onChange={(e) => {
        const pl = empresa.planes.find((x) => clavePlan(x) === e.target.value);
        if (pl) onElegir(pl);
      }}
      style={selectStyle}
    >
      {empresa.planes.map((p) => (
        <option key={clavePlan(p)} value={clavePlan(p)}>
          {etiquetaPlan(p, empresa.planes)}
          {p.esBase ? ' · base' : ''}
        </option>
      ))}
    </select>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 180 }}>
      <span
        className="text-element-mid"
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          fontWeight: 700,
        }}
      >
        {etiqueta}
      </span>
      {children}
    </div>
  );
}

function Fijo({ texto }: { texto: string }) {
  return <strong style={{ fontSize: 14 }}>{texto}</strong>;
}

const selectStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--color-outline, #d8dee9)',
  borderRadius: 6,
  fontSize: 13.5,
  minWidth: 200,
};
